// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { openMessage, useLocalMessage } from '@Helpers/';
import { useCurrentUser } from '@Queries/userQueries';
import { UserEventList } from '@Organisms/User/eventList';
import { UserOutput, AdminPermissionsEnum, ProfileOutput, PasswordPolicyOutput } from 'thehive-sdk';
import Details from '@Molecules/Details/Details';
import { extractData } from '@Organisms/User/extractData';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import THTag from '@Atoms/THTag/THTag';
import { HTMLColors } from '@Constants/colors';
import { THForm } from '@Molecules/THForm/THForm';
import moment from 'moment';
import useListQuery from '@Hooks/useListQuery';
import client, { queryClient } from '@Client';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { DisplayMetadata } from '@Molecules/DisplayMetadata/DisplayMetadata';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { FormatDate } from '@Molecules/Format';
import { ADMIN_ORG_ID } from '@Types';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import { useGenericQuery } from '@Queries/genericQueries';
import { computeEventList as computeAuthEventList } from '@Organisms/AccountDetails/eventList';
import { handleNotificationError } from '@Helpers/';
import { getUpdatedAvatar } from '@Atoms/UploadAvatar/UploadAvatar';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { computeValidatePassword } from '@Helpers/password';
import useValidateForm from '@Hooks/useValidateForm';
import { THPopConfirm } from '@Molecules/Inputs/THPopConfirm/THPopConfirm';
import { computeEventList as computeSessionEventList } from '@Organisms/Session/eventList';
import { checkPermissions } from '@Helpers/permissions';
import { ICONS } from '@Constants/iconsApp';

import './GlobalUserDrawer.scss';

interface GlobalUserDrawerProps {
    data: UserOutput;
    eventList: UserEventList;
    orgId?: string;
    visible: boolean;
    onVisibleChange: (visible: boolean) => void;
}

export const GlobalUserDrawer = ({
    data,
    eventList,
    orgId,
    visible,
    onVisibleChange,
}: GlobalUserDrawerProps): ReactElement => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange } = useValidateForm();
    const [fieldsTouched, setFieldsTouched] = useState(false);
    const { data: currentUser } = useCurrentUser();
    const {
        name,
        avatar,
        locked,
        _createdAt,
        _createdBy,
        _updatedAt,
        _id: userId,
        login,
        hasPassword,
        extraData,
    } = data;

    const { permissions = [] } = currentUser || {};
    const isBlocked = extraData?.lockout?.lockedUntil;

    const { data: profileList = [], refetch: refetchProfileList } = useListQuery<ProfileOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listProfile' }],
                name: 'get-all-profiles',
                sort: ['+name'],
            },
        },
        controller: client.ProfileController,
        manual: true,
        fetchOnStart: true,
    });

    const authEventList = useMemo(
        () =>
            computeAuthEventList({
                controller: client.AuthController,
                onSuccess: (count, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                },
                onError: handleNotificationError,
                locale: i,
            }),
        [i],
    );

    const sessionEventList = useMemo(
        () =>
            computeSessionEventList({
                controller: client.SessionController,
                onSuccess: (count, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                },
                onError: handleNotificationError,
                locale: i,
            }),
        [i],
    );

    const { data: passwordPolicy } = useGenericQuery<PasswordPolicyOutput>({
        fetchCallback: () => authEventList.onGetPasswordPolicy(),
        key: ['get-password-policy'],
        manual: true,
    });

    const { data: sessionList = [], refetch: refetchSessionList } = useGenericQuery({
        fetchCallback: () => sessionEventList.onListUserSessions(login),
        key: ['get-user-sessions', userId],
        manual: true,
        fetchOnStart: false,
    });

    const validator = useCallback(
        (str) => computeValidatePassword(passwordPolicy as PasswordPolicyOutput)(str),
        [passwordPolicy],
    );

    const profileListProcessed = useMemo(
        () => profileList.filter((profile) => (orgId === ADMIN_ORG_ID ? profile.forAdmin : profile.forOrg)),
        [profileList, orgId],
    );

    const forbiddenFields = useMemo(() => {
        const fields = [];
        if (login === 'admin@thehive.local' || login === currentUser?.login) {
            fields.push('locked', 'type');
        }
        if (!orgId) {
            fields.push('#computeProfile', '#computePermissions');
        } else {
            fields.push('organisations');
        }
        return fields;
    }, [login, orgId, currentUser]);

    const DeleteButton = useCallback(
        (toggleDrawer: () => void) => (
            <div className="global-user-drawer__footer-action">
                <THPopConfirm
                    title={i('user.drawer.delete.message')}
                    onConfirm={() => {
                        eventList.onDelete(userId, orgId);
                        toggleDrawer();
                    }}
                >
                    <THButton
                        disabled={locked || login === 'admin@thehive.local' || currentUser?.login === login}
                        color="red"
                        type="primary"
                    >
                        {i('user.drawer.delete.label')}
                    </THButton>
                </THPopConfirm>
                {isBlocked ? (
                    <THPopConfirm title={i('user.drawer.unBlock.confirm')} onConfirm={() => eventList.onUnblock(login)}>
                        <THButton type="default">{i('user.drawer.unBlock.label')}</THButton>
                    </THPopConfirm>
                ) : null}
            </div>
        ),
        [currentUser, eventList, i, locked, userId, orgId, login, isBlocked],
    );

    useEffect(() => {
        form.resetFields();
    }, [data, form]);

    return (
        <div className="generic-drawer-preview">
            <THDrawer
                title={
                    <div className="global-user-drawer__title">
                        <div className="global-user-drawer__title-avatar">
                            <UserAvatar name={name} avatar={avatar} />
                            {name}
                        </div>
                        <THTag
                            full
                            value={i(`user.drawer.status.${locked ? 'locked' : 'active'}`)}
                            color={locked ? HTMLColors.RED : isBlocked ? HTMLColors.AMBER : HTMLColors.GREEN}
                        />
                    </div>
                }
                className="drawer-form"
                disableClose={fieldsTouched}
                visible={visible}
                afterVisibleChange={(visible) => {
                    form.resetFields();
                    if (visible) {
                        refetchSessionList();
                        refetchProfileList();
                    }
                    onVisibleChange(visible);
                }}
            >
                {(toggleDrawer) => (
                    <THForm<UserOutput & { password: string; setPassword: boolean }>
                        form={form}
                        editable
                        autoComplete="off"
                        initialValues={{
                            ...data,
                            _createdAt: moment(_createdAt),
                            _updatedAt: moment(_updatedAt),
                            organisations: data.extraData?.organisations || [],
                        }}
                        onValuesChange={() => {
                            onFormValuesChange();
                            setFieldsTouched(true);
                        }}
                        onFinish={async ({
                            _createdAt, // eslint-disable-line
                            _updatedAt, // eslint-disable-line
                            organisations = [],
                            password,
                            setPassword,
                            avatar,
                            email = '',
                            ...data
                        }) => {
                            const processedAvatar = !avatar?.startsWith('api/v1')
                                ? getUpdatedAvatar(avatar)
                                : undefined;

                            if (!orgId) {
                                try {
                                    await eventList.onSetOrganisations(organisations, userId);
                                    queryClient.refetchQueries(['currentUser']);
                                } catch (e) {
                                    handleNotificationError(e);
                                }
                            }

                            if (password && setPassword) {
                                try {
                                    await eventList.onSetPassword(password, userId);
                                } catch (e) {
                                    handleNotificationError(e);
                                }
                            }

                            try {
                                await eventList.onEdit(
                                    {
                                        ...data,
                                        email: email || null,
                                        organisation: orgId,
                                        avatar: processedAvatar,
                                    },
                                    userId,
                                );

                                if (processedAvatar) {
                                    queryClient.refetchQueries(['currentUser']);
                                }
                            } catch (e) {
                                handleNotificationError(e);
                            }

                            setFieldsTouched(false);
                        }}
                    >
                        <THForm.Layout
                            header={
                                <DisplayMetadata
                                    items={[
                                        {
                                            icon: <ICONS.SettingIcon />,
                                            label: 'id',
                                            value: <CopyValue value={userId}>{userId}</CopyValue>,
                                        },
                                        {
                                            icon: <ICONS.UserIcon />,
                                            label: i('common.created-by'),
                                            value: <AssigneeInfo assignee={_createdBy} noAvatar />,
                                        },
                                        {
                                            icon: <ICONS.CreationDateIcon />,
                                            label: i('common.created-at'),
                                            value: <FormatDate date={_createdAt} />,
                                        },
                                        {
                                            icon: <ICONS.UpdateDateIcon />,
                                            label: i('common.updated-at'),
                                            value: <FormatDate date={_updatedAt} />,
                                            hidden: !_updatedAt,
                                        },
                                    ]}
                                />
                            }
                            footer={
                                fieldsTouched ? (
                                    <FormSubmitButton
                                        onCancel={() => {
                                            form.resetFields();
                                            setFieldsTouched(false);
                                        }}
                                        disabled={!isValid}
                                        hidden={!fieldsTouched}
                                    />
                                ) : (
                                    <THDrawer.Footer actions={DeleteButton(toggleDrawer)} />
                                )
                            }
                        >
                            <Details
                                data={{
                                    ...data,
                                    '#computeProfile': { profileList: profileListProcessed, refetchProfileList },
                                    '#computePermissions': {
                                        profileList: profileListProcessed,
                                    },
                                    '#computePassword': {
                                        passwordRule: passwordPolicy,
                                        validator,
                                        hasPassword,
                                    },
                                    '#computeResetPassword': {
                                        resetPassword: () => authEventList.sendPasswordReset(login),
                                    },
                                    '#computeSessions': sessionList.length
                                        ? {
                                              sessionList,
                                              onSuccess: refetchSessionList,
                                              onDelete: checkPermissions({
                                                  permissions,
                                                  permissionRequired: AdminPermissionsEnum.MANAGE_USER,
                                              })
                                                  ? sessionEventList.onDelete
                                                  : undefined,
                                          }
                                        : undefined,
                                }}
                                extract={extractData({
                                    locale: i,
                                    data,
                                    eventList,
                                    authEventList,
                                })}
                                forbidden={forbiddenFields}
                            />
                        </THForm.Layout>
                    </THForm>
                )}
            </THDrawer>
        </div>
    );
};
